import { parseJsonEventStream, uiMessageChunkSchema, type UIMessageChunk } from 'ai';

import { getAgentTransportConfig } from '@flintmint/web-shared/utils/llm-agent-utils';
import type { DocumentNameDecision, GenerationUsageMessageMetadata } from '@flintmint/core-shared/message-metadata';
import type { AgentRequestDocumentType } from '@flintmint/core-shared/constants';
import type { UserSource } from '@flintmint/core-shared/rxdb';
import { ENVIRONMENT } from '@/constants';

import { fetchAgent } from '../agent-fetch';

export class ServerStreamError extends Error {
  constructor(message: string, readonly origin: 'error' | 'abort') {
    super(message);
    this.name = 'ServerStreamError';
  }
}

type PromptModeRequest = {
  variables: Record<string, any>;
  userSources?: UserSource[];
  documentId?: string;
  documentType?: AgentRequestDocumentType;
};

type PromptModeTransportOptions = {
  getAccessToken: () => Promise<string | null>;
  signal?: AbortSignal;
};

type StreamPromptModeResult = {
  textStream: ReadableStream<string>;
  usage: Promise<GenerationUsageMessageMetadata | undefined>;
};

export async function streamPromptMode(
  prompt: 'generate-text',
  request: PromptModeRequest,
  options: PromptModeTransportOptions
): Promise<StreamPromptModeResult> {
  const response = await fetchPromptMode(prompt, request, options);
  const body = response.body;

  if (!body) {
    throw new Error('Prompt mode response did not include a stream body');
  }

  return decodeUIMessageStream(body);
}

// Structural stand-in for @ai-sdk/provider-utils' ParseResult, which `ai` does not re-export.
type UIMessageChunkParseResult = { success: true; value: UIMessageChunk } | { success: false; error: Error };

function decodeUIMessageStream(body: ReadableStream<Uint8Array>): StreamPromptModeResult {
  let resolveUsage!: (usage: GenerationUsageMessageMetadata | undefined) => void;
  const usage = new Promise<GenerationUsageMessageMetadata | undefined>((resolve) => {
    resolveUsage = resolve;
  });

  const textStream = parseJsonEventStream({ stream: body, schema: uiMessageChunkSchema }).pipeThrough(
    new TransformStream<UIMessageChunkParseResult, string>({
      transform(parseResult, controller) {
        if (!parseResult.success) {
          resolveUsage(undefined);
          controller.error(parseResult.error);
          return;
        }

        const chunk = parseResult.value;
        if (chunk.type === 'text-delta') {
          controller.enqueue(chunk.delta);
        } else if (chunk.type === 'finish') {
          resolveUsage(chunk.messageMetadata as GenerationUsageMessageMetadata | undefined);
        } else if (chunk.type === 'error') {
          resolveUsage(undefined);
          controller.error(new ServerStreamError(chunk.errorText, 'error'));
        } else if (chunk.type === 'abort') {
          resolveUsage(undefined);
          controller.error(new ServerStreamError('Generation aborted', 'abort'));
        }
      },
      flush() {
        resolveUsage(undefined);
      },
    })
  );

  return { textStream, usage };
}

export async function runPromptMode(
  prompt: 'generate-document-name',
  request: PromptModeRequest,
  options: PromptModeTransportOptions
): Promise<DocumentNameDecision> {
  const response = await fetchPromptMode(prompt, request, options);
  const data = (await response.json()) as { response?: unknown };

  if (!isDocumentNameDecision(data.response)) {
    throw new Error('Prompt mode response did not include a document name decision');
  }

  return data.response;
}

function isDocumentNameDecision(value: unknown): value is DocumentNameDecision {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const { violatedNameRule, name } = value as Record<string, unknown>;
  return (typeof violatedNameRule === 'string' || violatedNameRule === null) && (typeof name === 'string' || name === null);
}

async function fetchPromptMode(
  prompt: 'generate-text' | 'generate-document-name',
  { variables, userSources, documentId, documentType }: PromptModeRequest,
  { getAccessToken, signal }: PromptModeTransportOptions
): Promise<Response> {
  const token = await getAccessToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  // Reused for the api URL only — headers are built directly from the token already resolved
  // above, so there's no second async resolution to stay in sync with.
  const { api } = getAgentTransportConfig(getAccessToken, ENVIRONMENT);
  const response = await fetchAgent(buildPromptModeUrl(api, prompt), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      variables,
      ...(userSources ? { userSources } : {}),
      ...(documentId ? { documentId } : {}),
      ...(documentType ? { documentType } : {}),
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(await getPromptModeErrorMessage(response));
  }

  return response;
}

function buildPromptModeUrl(api: string, prompt: 'generate-text' | 'generate-document-name'): string {
  return `${api.replace(/\/$/, '')}/${prompt}`;
}

async function getPromptModeErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string' && body.error.trim()) {
      return body.error;
    }
  } catch {
    // Fall back to status text below.
  }

  return response.statusText || `Prompt mode request failed with status ${response.status}`;
}
